import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import ReactPlayer from 'react-player/lazy'
import { useIntl, FormattedMessage } from 'react-intl'
import useAudioPlayer from './hooks/useAudioPlayer'
import { evaluateInteractive } from './hooks/useLessonScoring'
import { CapeeshColors } from '../../../../assets/ColorPalette'
import renderFormattedText from './utils/renderFormattedText'
import { playerMessages } from './CapeeshPlayer.i18n.Messages'

const InteractiveElementType = {
    MultiChoice: 1,
    VideoInteraction: 18,
}

const AudioPlayType = {
    DontPlay: 0,
    PlayBefore: 1,
    PlayAfter: 2,
}

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        gap: 16,
        padding: '12px 20px 20px',
        overflow: 'auto',
    },
    videoContainer: {
        width: '100%',
        maxWidth: 600,
        height: 550,
        borderRadius: 16,
        overflow: 'hidden',
        backgroundColor: '#000',
        flexShrink: 0,
        resize: 'both',
        minHeight: 200,
        minWidth: 280,
    },
    headerSection: {
        width: '100%',
        maxWidth: 480,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
    },
    titleText: {
        fontFamily: 'Rubik',
        fontSize: 20,
        fontWeight: 600,
        color: '#FFFFFF',
        textAlign: 'center',
    },
    progressText: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 400,
        color: 'rgba(255, 255, 255, 0.5)',
    },
    questionCard: {
        width: '100%',
        maxWidth: 480,
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        borderRadius: 20,
        padding: '24px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        flexShrink: 0,
    },
    questionImage: {
        maxWidth: '100%',
        maxHeight: 200,
        borderRadius: 12,
        objectFit: 'contain',
    },
    questionText: {
        fontFamily: 'Rubik',
        fontSize: 18,
        fontWeight: 500,
        color: '#FFFFFF',
        textAlign: 'center',
    },
    optionsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        width: '100%',
        maxWidth: 480,
        flexShrink: 0,
    },
    optionButton: {
        width: '100%',
        padding: '14px 18px',
        borderRadius: 14,
        border: '2px solid rgba(255, 255, 255, 0.3)',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 500,
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'all 200ms ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.16)',
            borderColor: 'rgba(255, 255, 255, 0.5)',
        },
    },
    optionCorrect: {
        borderColor: CapeeshColors.GreenBright,
        backgroundColor: 'rgba(128, 211, 135, 0.25)',
        '&:hover': {
            backgroundColor: 'rgba(128, 211, 135, 0.25)',
        },
    },
    optionIncorrect: {
        borderColor: CapeeshColors.PinkBright,
        backgroundColor: 'rgba(255, 102, 110, 0.25)',
        '&:hover': {
            backgroundColor: 'rgba(255, 102, 110, 0.25)',
        },
    },
    optionDimmed: {
        opacity: 0.4,
        cursor: 'default',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
        },
    },
    optionExamSelected: {
        borderColor: 'rgba(255, 255, 255, 0.45)',
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
        },
    },
    feedbackText: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 500,
        textAlign: 'center',
    },
    feedbackCorrect: {
        color: CapeeshColors.GreenBright,
    },
    feedbackIncorrect: {
        color: CapeeshColors.PinkBright,
    },
    continueButton: {
        padding: '14px 48px',
        borderRadius: 28,
        border: 'none',
        backgroundColor: '#FFFFFF',
        color: '#1A1A2E',
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
        marginTop: 16,
        transition: 'transform 150ms ease',
        '&:hover': {
            transform: 'translateY(-1px)',
        },
    },
}))

const VideoNode = ({ node, previousNode, imageMap, audioMap, onNodeComplete, isExamMode, isNativeSpeaker = false, l2LanguageId }) => {
    const classes = useStyles()
    const intl = useIntl()
    const playAfterAudioPlayer = useAudioPlayer()
    const questionStartTime = useRef(Date.now())

    const testOptions = node.testOptions || []

    const videoOption = useMemo(
        () => testOptions.find(
            (to) => to.scenarioInteractiveElementType === InteractiveElementType.VideoInteraction
                || to.interactiveElementType === InteractiveElementType.VideoInteraction
        ),
        [testOptions]
    )

    const videoUrl = videoOption?.videoUrl || ''

    const multiChoiceOptions = useMemo(
        () => testOptions.filter(
            (to) => to.scenarioInteractiveElementType === InteractiveElementType.MultiChoice
                || to.interactiveElementType === InteractiveElementType.MultiChoice
        ),
        [testOptions]
    )

    const hasQuestions = multiChoiceOptions.length > 0

    const [videoEnded, setVideoEnded] = useState(false)
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
    const [disabledAlternatives, setDisabledAlternatives] = useState(new Set())
    const [correctIdx, setCorrectIdx] = useState(null)
    const [lastWrongIdx, setLastWrongIdx] = useState(null)
    const [isAdvancing, setIsAdvancing] = useState(false)
    const interactiveAnswersRef = useRef([])
    const interactiveResultsRef = useRef([])

    const currentQuestion = multiChoiceOptions[currentQuestionIndex]
    const alternatives = currentQuestion?.alternatives || []

    const shuffledOrder = useMemo(() => {
        const indices = alternatives.map((_, i) => i)
        for (let i = indices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[indices[i], indices[j]] = [indices[j], indices[i]]
        }
        return indices
    }, [currentQuestionIndex, alternatives.length])

    useEffect(() => {
        setDisabledAlternatives(new Set())
        setCorrectIdx(null)
        setLastWrongIdx(null)
        setIsAdvancing(false)
        interactiveAnswersRef.current = []
        questionStartTime.current = Date.now()
    }, [currentQuestionIndex])

    const handleVideoEnded = useCallback(() => {
        setVideoEnded(true)
    }, [])

    const advanceToNext = useCallback(() => {
        if (currentQuestionIndex < multiChoiceOptions.length - 1) {
            setCurrentQuestionIndex((prev) => prev + 1)
        } else {
            playAfterAudioPlayer.stopAudio()
            onNodeComplete(
                {
                    nodeId: node.nodeId,
                    chosenAlternativeIndex: 0,
                },
                interactiveResultsRef.current
            )
        }
    }, [currentQuestionIndex, multiChoiceOptions.length, node.nodeId, onNodeComplete, playAfterAudioPlayer])

    const handleSelect = useCallback(
        (origIdx) => {
            if (isAdvancing || correctIdx !== null) return
            if (disabledAlternatives.has(origIdx)) return

            const alt = alternatives[origIdx]
            const isCorrect = alt?.correct === true

            const timeSpent = (Date.now() - questionStartTime.current) / 1000
            const answer = {
                testOptionId: currentQuestion.testOptionId,
                answer: alt?.text || '',
                correct: isCorrect,
                relativeTestOptionTime: timeSpent,
            }
            interactiveAnswersRef.current.push(answer)

            if (isCorrect || isExamMode) {
                setCorrectIdx(origIdx)
                setLastWrongIdx(null)
                setIsAdvancing(true)

                const evaluation = evaluateInteractive(currentQuestion, interactiveAnswersRef.current, isExamMode)
                interactiveResultsRef.current.push({
                    ...evaluation,
                    answers: [...interactiveAnswersRef.current],
                })

                const playAfterUrl = currentQuestion.audioPlayType === AudioPlayType.PlayAfter
                    && currentQuestion.textAudioUrl
                    ? (audioMap[currentQuestion.textAudioUrl] || null)
                    : null

                if (playAfterUrl && isCorrect) {
                    playAfterAudioPlayer.playAudio(playAfterUrl).then(() => {
                        setTimeout(advanceToNext, 200)
                    })
                } else {
                    setTimeout(advanceToNext, 1200)
                }
            } else {
                setLastWrongIdx(origIdx)
                setDisabledAlternatives((prev) => new Set([...prev, origIdx]))
                setTimeout(() => setLastWrongIdx(null), 800)
            }
        },
        [
            isAdvancing,
            correctIdx,
            disabledAlternatives,
            alternatives,
            currentQuestion,
            isExamMode,
            audioMap,
            advanceToNext,
            playAfterAudioPlayer,
        ]
    )

    const allTexts = (node.texts || []).filter(
        (t) => !isNativeSpeaker || !l2LanguageId || t.textLanguageId === l2LanguageId || !t.textLanguageId
    )
    const titleTexts = allTexts.filter(
        (t) => t.textType === 1 || t.textType === 2
    )

    const questionImageUrl = currentQuestion?.imageUrl
        ? (imageMap[currentQuestion.imageUrl] || null)
        : null

    // No questions: video-only mode with continue on end
    if (!hasQuestions) {
        return (
            <div className={classes.container}>
                {titleTexts.length > 0 && (
                    <div className={classes.headerSection}>
                        {titleTexts.map((t, i) => (
                            <Typography key={i} className={classes.titleText}>
                                {renderFormattedText(t.text)}
                            </Typography>
                        ))}
                    </div>
                )}

                {videoUrl && (
                    <div className={classes.videoContainer}>
                        <ReactPlayer
                            url={videoUrl}
                            width="100%"
                            height="100%"
                            controls
                            playing
                            onEnded={handleVideoEnded}
                            config={{
                                youtube: { playerVars: { modestbranding: 1 } },
                                vimeo: { playerOptions: { byline: false, portrait: false } },
                            }}
                        />
                    </div>
                )}

                <button
                    className={classes.continueButton}
                    onClick={() =>
                        onNodeComplete(
                            {
                                nodeId: node.nodeId,
                                chosenAlternativeIndex: 0,
                            },
                            interactiveResultsRef.current
                        )
                    }
                >
                    <FormattedMessage {...playerMessages.continueButton} />
                </button>
            </div>
        )
    }

    // Video + quiz mode
    return (
        <div className={classes.container}>
            {/* Video player */}
            {videoUrl && (
                <div className={classes.videoContainer}>
                    <ReactPlayer
                        url={videoUrl}
                        width="100%"
                        height="100%"
                        controls
                        playing
                        onEnded={() => setVideoEnded(true)}
                        config={{
                            youtube: { playerVars: { modestbranding: 1 } },
                            vimeo: { playerOptions: { byline: false, portrait: false } },
                        }}
                    />
                </div>
            )}

            {/* Header */}
            <div className={classes.headerSection}>
                {titleTexts.map((t, i) => (
                    <Typography key={i} className={classes.titleText}>
                        {renderFormattedText(t.text)}
                    </Typography>
                ))}
                {multiChoiceOptions.length > 1 && (
                    <Typography className={classes.progressText}>
                        {currentQuestionIndex + 1} / {multiChoiceOptions.length}
                    </Typography>
                )}
            </div>

            {/* Question card */}
            {currentQuestion && (
                <div className={classes.questionCard}>
                    {questionImageUrl && (
                        <img
                            src={questionImageUrl}
                            alt=""
                            className={classes.questionImage}
                        />
                    )}
                    {currentQuestion.text && (
                        <Typography className={classes.questionText}>
                            {renderFormattedText(currentQuestion.text)}
                        </Typography>
                    )}
                </div>
            )}

            {/* Options */}
            {currentQuestion && (
                <div className={classes.optionsContainer}>
                    {shuffledOrder.map((origIdx) => {
                        const alt = alternatives[origIdx]
                        if (!alt) return null

                        const isDisabled = disabledAlternatives.has(origIdx)
                        const isWrongJustSelected = lastWrongIdx === origIdx
                        const isCorrectSelected = correctIdx === origIdx

                        let extraClass = ''
                        if (isExamMode) {
                            if (isCorrectSelected) {
                                extraClass = classes.optionExamSelected
                            } else if (isDisabled) {
                                extraClass = classes.optionDimmed
                            }
                        } else {
                            if (isCorrectSelected) {
                                extraClass = classes.optionCorrect
                            } else if (isWrongJustSelected) {
                                extraClass = classes.optionIncorrect
                            } else if (isDisabled) {
                                extraClass = classes.optionDimmed
                            } else if (correctIdx !== null) {
                                extraClass = classes.optionDimmed
                            }
                        }

                        return (
                            <button
                                key={origIdx}
                                className={`${classes.optionButton} ${extraClass}`}
                                onClick={() => handleSelect(origIdx)}
                                disabled={isDisabled || correctIdx !== null}
                            >
                                {alt.text || intl.formatMessage(playerMessages.optionNumber, { number: origIdx + 1 })}
                            </button>
                        )
                    })}
                </div>
            )}

            {/* Feedback */}
            {correctIdx !== null && !isExamMode && (
                <Typography className={`${classes.feedbackText} ${classes.feedbackCorrect}`}>
                    <FormattedMessage {...playerMessages.correctFeedback} />
                </Typography>
            )}
            {lastWrongIdx !== null && !isExamMode && (
                <Typography className={`${classes.feedbackText} ${classes.feedbackIncorrect}`}>
                    <FormattedMessage {...playerMessages.incorrectFeedback} />
                </Typography>
            )}

            <button
                className={classes.continueButton}
                onClick={() => {
                    playAfterAudioPlayer.stopAudio()
                    onNodeComplete(
                        {
                            nodeId: node.nodeId,
                            chosenAlternativeIndex: 0,
                        },
                        interactiveResultsRef.current
                    )
                }}
            >
                <FormattedMessage {...playerMessages.continueButton} />
            </button>
        </div>
    )
}

export default VideoNode
